// frontend/src/components/EnhancedInsuranceIcons/TenantInsuranceIcon.jsx
import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import styles from './EnhancedInsuranceIcons.module.css';

const TenantInsuranceIcon = ({ size = 120 }) => {
  const buildingRef = useRef(null);
  
  useEffect(() => {
    if (!buildingRef.current) return;
    
    const structure = buildingRef.current.querySelector('#building-structure');
    const windows = buildingRef.current.querySelector('#building-windows');
    const highlightedUnit = buildingRef.current.querySelector('#highlighted-unit');
    const door = buildingRef.current.querySelector('#building-door');
    const furniture = buildingRef.current.querySelector('#furniture');
    const key = buildingRef.current.querySelector('#key');
    
    // Hide all elements initially
    gsap.set([structure, windows, highlightedUnit, door, furniture, key], { 
      opacity: 0,
      y: -20
    });
    
    // Create animation timeline
    const tl = gsap.timeline({ defaults: { ease: "back.out(1.7)" } });
    
    // Animate each part dropping in with slight bounce effect
    tl.to(structure, { opacity: 1, y: 0, duration: 0.6, delay: 0.2 })
      .to(windows, { opacity: 0.7, y: 0, duration: 0.5, stagger: 0.05 }, "-=0.3")
      .to(door, { opacity: 1, y: 0, duration: 0.4 }, "-=0.2")
      .to(highlightedUnit, { opacity: 1, y: 0, duration: 0.6 }, "-=0.2")
      .to(furniture, { opacity: 1, y: 0, duration: 0.4 }, "-=0.2")
      .to(key, { opacity: 1, y: 0, duration: 0.4, rotation: 20 }, "-=0.2");
    
    // Add subtle continuous animation for highlighted unit
    gsap.to(highlightedUnit, {
      opacity: 0.8,
      duration: 1.5,
      repeat: -1,
      yoyo: true,
      ease: "sine.inOut"
    });
    
    // Add key animation
    gsap.to(key, {
      rotation: -10,
      duration: 2,
      repeat: -1,
      yoyo: true,
      ease: "sine.inOut"
    });
    
    return () => {
      // Clean up animations when component unmounts
      tl.kill();
      gsap.killTweensOf([structure, windows, highlightedUnit, door, furniture, key]);
    };
  }, []);
  
  return (
    <div className={styles.iconContainer} ref={buildingRef} style={{ width: size, height: size }}>
      <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
        {/* Building structure */}
        <rect id="building-structure" x="20" y="20" width="60" height="70" fill="#DEE0E3" rx="2" filter="url(#shadow)" />
        
        {/* Windows grid */}
        <g id="building-windows" fill="#A0C4FF" opacity="0.7">
          {/* First row */}
          <rect x="25" y="25" width="10" height="10" rx="1" />
          <rect x="45" y="25" width="10" height="10" rx="1" />
          <rect x="65" y="25" width="10" height="10" rx="1" />
          
          {/* Second row */}
          <rect x="25" y="40" width="10" height="10" rx="1" />
          <rect x="65" y="40" width="10" height="10" rx="1" />
          
          {/* Third row */}
          <rect x="25" y="55" width="10" height="10" rx="1" />
          <rect x="65" y="55" width="10" height="10" rx="1" />
        </g>
        
        {/* Highlighted apartment unit */}
        <rect 
          id="highlighted-unit" 
          x="45" 
          y="40" 
          width="10" 
          height="10" 
          rx="1" 
          fill="#38C793" 
          opacity="0.9"
          filter="url(#glow)" 
        />
        
        {/* Front door */}
        <rect id="building-door" x="43" y="75" width="14" height="15" rx="1" fill="#5E636E" />
        
        {/* Furniture (simplified) */}
        <g id="furniture" transform="translate(45, 40)" fill="#F2AE40">
          <rect x="0" y="0" width="6" height="3" rx="1" />
          <rect x="1" y="3" width="4" height="4" rx="1" />
        </g>
        
        {/* Key */}
        <g id="key" transform="translate(25, 50)">
          <circle cx="0" cy="0" r="3" fill="#F17B2C" />
          <rect x="3" y="-1" width="8" height="2" fill="#F17B2C" />
          <rect x="8" y="-3" width="2" height="6" fill="#F17B2C" />
        </g>
        
        {/* Filters */}
        <defs>
          <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="3" stdDeviation="2" floodOpacity="0.2" />
          </filter>
          <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="1" result="blur" />
            <feFlood floodColor="#38C793" floodOpacity="0.5" />
            <feComposite in2="blur" operator="in" />
            <feComposite in="SourceGraphic" />
          </filter>
        </defs>
      </svg>
    </div>
  );
};

export default TenantInsuranceIcon;